import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CompanyUnitOfMeasureRecord } from '../graphql/companyUnitOfMeasureQueries';
import {
    addCompanyUnitOfMeasureFromLabel,
    ensureCompanyUnitsOfMeasureSeeded,
} from '../utils/companyUnitOfMeasureCatalog';

export function useCompanyUnitsOfMeasure(companyId: string, extraValues: string[] = []) {
    const [items, setItems] = useState<CompanyUnitOfMeasureRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const extraKey = extraValues.join('|');
    const extraValuesRef = useRef(extraValues);
    extraValuesRef.current = extraValues;

    const refresh = useCallback(async () => {
        if (!companyId) {
            setItems([]);
            return [];
        }
        setLoading(true);
        try {
            const seeded = await ensureCompanyUnitsOfMeasureSeeded(companyId, extraValuesRef.current);
            setItems(seeded);
            return seeded;
        } catch (error) {
            console.error('Failed to load units of measure:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        let isCancelled = false;
        if (!companyId) {
            setItems([]);
            return;
        }
        setLoading(true);
        (async () => {
            try {
                const seeded = await ensureCompanyUnitsOfMeasureSeeded(companyId, extraValuesRef.current);
                if (isCancelled) return;
                setItems(seeded);
            } catch (error) {
                if (isCancelled) return;
                console.error('Failed to load units of measure:', error);
            } finally {
                if (!isCancelled) setLoading(false);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId, extraKey]);

    const createUnit = useCallback(
        async (rawLabel: string) => {
            const created = await addCompanyUnitOfMeasureFromLabel(companyId, rawLabel, items);
            setItems((prev) => {
                if (prev.some((item) => item.id === created.id)) return prev;
                return [...prev, created];
            });
            return created;
        },
        [companyId, items],
    );

    const catalogOptions = useMemo(
        () => items.map((item) => ({ value: item.value, label: item.name })),
        [items],
    );

    return { items, catalogOptions, loading, refresh, createUnit };
}
